import React, { useEffect, useRef, useState } from 'react';
import { useJourneyStore } from '@src/pages/journey-explorer/store';
import * as echarts from 'echarts';
import { parseNumToPercentage } from '@src/shared/utils';
import { BarChartOutlined, CloseOutlined, LoadingOutlined, MinusOutlined } from '@ant-design/icons';
import { getJourneyStageAggregatedMetrics } from '@src/pages/journey-map/utils/journey/journeyMetrics';
import { Button } from 'antd';
import {
  MiniMap,
  Header,
  IconWrapper,
  ChartContainer,
  ViewDetailsButton,
  StatsContainer,
  StatItem,
  StatValue,
  StatLabel,
  MinimizedContainer,
  MinimizedHeader,
  MinimizedIcon,
  MinimizedStats,
  MinimizedStatItem,
  MinimizedStatValue,
  MinimizedExpandHint,
} from './styled';
import { useNavigate } from 'react-router-dom';
import { usePatternQuery } from '@src/services/queries';
import { appTracker } from '@src/shared/utils/sensor/apptracker';
import { CHART_COLORS } from '@src/shared/constants/color';
import { useDatasetStore } from '@src/shared/store';
import { NoData } from '@src/shared/components';
import ActivateFlow from '../ActivateFlow';
import type { FilterCondition } from '@src/shared/types';
import { TIMEOUT_COEFFICIENTS } from '@src/shared/constants/analysis';
import { readableTime } from '@src/pages/journey-map/utils/formatTime';

interface IProps {
  steps?: string[];
  filters: FilterCondition[][];
  topPaths: number;
  interval: [string, string];
  timeout: number;
  timeoutUnit: string;
}

const MiniFunnel: React.FC<IProps> = ({
  steps,
  filters,
  topPaths,
  interval,
  timeout,
  timeoutUnit,
}) => {
  const [minimized, setMinimized] = useState(false);
  const [visible, setVisible] = useState(false);
  const { selectedNodes, globalFilter } = useJourneyStore();
  useEffect(() => {
    if (selectedNodes.length > 0) {
      setVisible(true);
    }
  }, [selectedNodes]);
  const { paths } = useDatasetStore();
  const navigate = useNavigate();
  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstance = useRef<echarts.ECharts | null>(null);

  const data = getJourneyStageAggregatedMetrics(paths, steps || []);

  // Use query-based hook with automatic caching
  const patternQueryPayload = {
    pattern: steps && steps.length >= 2 ? steps : [],
    time_range: interval,
    timeout_ms: timeout * TIMEOUT_COEFFICIENTS[timeoutUnit],
    filters: filters,
    aggregations: ['user_conversion_rate', 'mean_time_since_beginning_ms'],
  };

  const { data: funnelData = [], isFetching } = usePatternQuery(patternQueryPayload);

  useEffect(() => {
    if (funnelData && funnelData.length > 0 && funnelData.length !== steps?.length) {
      appTracker({
        name: 'Mini Funnel View Error',
        data: {
          funnelSteps: steps?.join(','),
          funnelData: funnelData,
          globalFilter: globalFilter
            ?.map((filterGroup) => filterGroup.map((f) => `${f.field}:${f.val}`).join(' AND '))
            .join(' OR '),
          topPaths: topPaths,
        },
      });
    }
  }, [funnelData, steps, globalFilter, topPaths]);

  useEffect(() => {
    if (selectedNodes.length === 0) {
      setMinimized(false);
    }
  }, [selectedNodes.length]);

  useEffect(() => {
    if (isFetching) {
      return;
    }
    if (chartRef.current && steps?.length && funnelData?.length) {
      chartInstance.current = echarts.init(chartRef.current);
      const xAxisData = steps?.map((step, index) => `S${index + 1}`);
      const value = 100;
      const conversionData = funnelData?.map((i, index) => {
        const newValue = value * i.user_conversion_rate;
        return {
          ...i,
          index,
          value: newValue,
        };
      });

      const option = {
        tooltip: {
          show: false,
          trigger: 'axis',
          axisPointer: {
            type: 'shadow',
          },
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          borderColor: 'transparent',
          textStyle: {
            color: '#fff',
            fontSize: 12,
          },
          formatter: function (params: any) {
            const prob = params[0].data.prob;
            return `${parseNumToPercentage(prob, 2)}<br/>`;
          },
        },
        grid: {
          left: '8%',
          right: '8%',
          bottom: '5px',
          top: '10%',
          containLabel: true,
        },
        xAxis: {
          type: 'category',
          data: xAxisData,
          axisLabel: {
            interval: 0,
            rotate: 0,
            color: 'rgba(0, 0, 0, 0.65)',
            fontWeight: 500,
            fontSize: 10,
          },
          axisLine: {
            lineStyle: {
              color: 'rgba(0, 0, 0, 0.1)',
            },
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: 'value',
          axisLabel: {
            formatter: function (value: number) {
              return value.toFixed(0) + '%';
            },
            fontSize: 10,
            color: 'rgba(0, 0, 0, 0.45)',
          },
          splitLine: {
            lineStyle: {
              color: 'rgba(0, 0, 0, 0.06)',
              type: 'dashed',
            },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        series: [
          {
            name: 'Pattern Analytics',
            type: 'bar',
            data: conversionData,
            itemStyle: {
              color: (params: any) => CHART_COLORS[params.dataIndex % CHART_COLORS.length],
              borderRadius: [4, 4, 0, 0],
            },
            label: {
              show: true,
              position: 'top',
              formatter: function (params: any) {
                if (params.dataIndex === 0) {
                  return '100%';
                } else {
                  const prob = params.value / 100;
                  if (prob === 0) {
                    return '0%';
                  }
                  if (prob < 0.0001) {
                    return '< 0.01%';
                  }
                  return parseNumToPercentage(prob, 2);
                }
              },
              fontSize: 9,
              color: 'rgba(0, 0, 0, 0.65)',
              fontWeight: 600,
            },
            barWidth: '50%',
            emphasis: {
              itemStyle: {
                shadowBlur: 10,
                shadowOffsetX: 0,
                shadowColor: 'rgba(0, 0, 0, 0.3)',
              },
            },
          },
        ],
      };

      chartInstance.current.setOption(option as any);
    }

    return () => {
      if (chartInstance.current) {
        // chartInstance.current.dispose();
      }
    };
  }, [steps, data, isFetching, funnelData]);

  if (!steps || !steps.length || steps.length < 2) return null;

  const totalSteps = steps.length;
  const steData = funnelData?.[totalSteps - 1];
  const completionRate = funnelData?.length > 0 ? steData?.user_conversion_rate * 100 : 0;
  const duration = steData?.mean_time_since_beginning_ms;

  const onViewDetails = () => {
    void navigate('/funnels/temp', {
      state: {
        critical_events: steps,
        filters: filters,
        time_range: interval,
        timeout: {
          val: timeout,
          unit: timeoutUnit,
        },
        propagation_config: Object.fromEntries(
          filters.flat().map(({ field }) => [field, 'context']),
        ),
      },
    });
    appTracker({
      name: 'Mini Funnel View Details',
      data: {
        funnelSteps: steps.join(','),
        completionRate: parseNumToPercentage(completionRate),
        globalFilter: globalFilter
          ?.map((filterGroup) => filterGroup.map((f) => `${f.field}:${f.val}`).join(' AND '))
          .join(' OR '),
        topPaths: topPaths,
      },
    });
  };

  const noData = !isFetching && funnelData && funnelData.length === 0;
  if (!visible) return null;

  return minimized ? (
    <MinimizedContainer onClick={() => setMinimized(false)}>
      <MinimizedHeader>
        <MinimizedIcon>
          <BarChartOutlined />
        </MinimizedIcon>
        <span>Funnel</span>
      </MinimizedHeader>
      <MinimizedStats>
        <MinimizedStatItem>
          <span>Steps:</span>
          <MinimizedStatValue>{totalSteps}</MinimizedStatValue>
        </MinimizedStatItem>
        <MinimizedStatItem>
          <span>Completion:</span>
          <MinimizedStatValue>
            {isFetching ? <LoadingOutlined /> : parseNumToPercentage(completionRate / 100, 2)}
          </MinimizedStatValue>
        </MinimizedStatItem>
      </MinimizedStats>
      <MinimizedExpandHint>click to expand</MinimizedExpandHint>
    </MinimizedContainer>
  ) : (
    <MiniMap>
      <Header>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span>Funnel Preview</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <IconWrapper onClick={() => setMinimized(true)}>
            <MinusOutlined />
          </IconWrapper>
          <IconWrapper
            onClick={() => {
              setVisible(false);
            }}
          >
            <CloseOutlined />
          </IconWrapper>
        </div>
      </Header>
      <StatsContainer>
        <StatItem>
          <StatLabel>User %</StatLabel>
          <StatValue>
            {isFetching ? <LoadingOutlined /> : parseNumToPercentage(completionRate / 100, 2)}
          </StatValue>
        </StatItem>
        {duration && (
          <StatItem>
            <StatLabel>Duration</StatLabel>
            <StatValue>{isFetching ? <LoadingOutlined /> : readableTime(duration)}</StatValue>
          </StatItem>
        )}
      </StatsContainer>
      <ChartContainer>
        {noData && <NoData type="chart" message="No data" />}
        <div
          ref={chartRef}
          style={{
            width: '100%',
            height: '100%',
            minHeight: noData ? '' : '150px',
          }}
        />
      </ChartContainer>
      <ViewDetailsButton>
        <Button type="default" color="primary" onClick={onViewDetails}>
          View Detailed Analysis
        </Button>
        <ActivateFlow funnelSteps={steps} />
      </ViewDetailsButton>
    </MiniMap>
  );
};

export default MiniFunnel;
